import { useEffect, useState } from "react";

/**
 * Tracks whether the page is being scrolled down or up so sticky chrome can
 * get out of the way on phones.
 *
 * - "up"  : header should be visible (also the initial state and near the top)
 * - "down": user is reading downward -> header can hide
 *
 * rAF-throttled, passive listener, ignores jitter smaller than `threshold`
 * pixels, and never reports "down" inside the first `topOffset` pixels so the
 * header is always present at the top of the page. SSR-safe.
 */
export const useScrollDirection = ({ threshold = 8, topOffset = 80 } = {}) => {
  const [direction, setDirection] = useState("up");

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    let lastY = window.scrollY;
    let ticking = false;

    const update = () => {
      const y = window.scrollY;
      const delta = y - lastY;
      if (Math.abs(delta) >= threshold) {
        setDirection(y <= topOffset || delta < 0 ? "up" : "down");
        lastY = y;
      }
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold, topOffset]);

  return direction;
};
